import { EventProperties } from "@/types/analytics";

interface DeduplicationConfig {
  windowMs: number;
  maxTrackedKeys: number;
  debug: boolean;
}

const eventEmissionMap = new Map<string, number>();

const defaultConfig: DeduplicationConfig = {
  windowMs: 1000,
  maxTrackedKeys: 1000,
  debug: false,
};

let currentConfig = { ...defaultConfig };

export function initializeDeduplication(config: Partial<DeduplicationConfig> = {}) {
  currentConfig = { ...defaultConfig, ...config };
  if (currentConfig.debug) {
    console.log("[EventDedup] Initialized with config:", currentConfig);
  }
}

export function generateEventKey(eventName: string, properties?: EventProperties, includeTimestamp: boolean = false): string {
  const parts = [eventName];

  if (properties?.formName) {
    parts.push(String(properties.formName));

    if (typeof properties.formStep === "number") {
      parts.push(String(properties.formStep));
    }

    if (properties.formField) {
      parts.push(String(properties.formField));
    }
  }

  if (properties?.fromStep !== undefined && properties?.toStep !== undefined) {
    parts.push(`${properties.fromStep}_to_${properties.toStep}`);
  }

  if (includeTimestamp) {
    parts.push(String(Date.now()));
  }

  return parts.join("_");
}

export function isEventDuplicate(eventKey: string): boolean {
  const now = Date.now();
  const lastEmitTime = eventEmissionMap.get(eventKey);

  if (lastEmitTime === undefined) {
    recordEventSent(eventKey);
    return false;
  }

  const timeSinceLastEmit = now - lastEmitTime;
  const isDuplicate = timeSinceLastEmit < currentConfig.windowMs;

  if (currentConfig.debug) {
    console.log(`[EventDedup] Key: "${eventKey}", TimeSince: ${timeSinceLastEmit}ms, IsDuplicate: ${isDuplicate}`);
  }

  if (!isDuplicate) {
    recordEventSent(eventKey);
  }

  return isDuplicate;
}

export function recordEventSent(eventKey: string): void {
  eventEmissionMap.set(eventKey, Date.now());

  if (eventEmissionMap.size > currentConfig.maxTrackedKeys) {
    const firstKey = eventEmissionMap.keys().next().value;
    if (firstKey) {
      eventEmissionMap.delete(firstKey);
      if (currentConfig.debug) {
        console.warn(`[EventDedup] Evicted oldest key to prevent memory leak: ${firstKey}`);
      }
    }
  }
}

export function isDeduplicated(eventKey: string): boolean {
  return isEventDuplicate(eventKey);
}

export function clearDeduplicationMap(): void {
  eventEmissionMap.clear();
  if (currentConfig.debug) {
    console.log("[EventDedup] Deduplication map cleared");
  }
}

export function getDeduplicationState(): Array<[string, number]> {
  return Array.from(eventEmissionMap.entries());
}

export function getDeduplicationStats(): {
  trackedKeysCount: number;
  oldestKeyAge: number;
  config: DeduplicationConfig;
} {
  let oldestKeyAge = 0;
  if (eventEmissionMap.size > 0) {
    const oldestTime = Math.min(...Array.from(eventEmissionMap.values()));
    oldestKeyAge = Date.now() - oldestTime;
  }

  return {
    trackedKeysCount: eventEmissionMap.size,
    oldestKeyAge,
    config: currentConfig,
  };
}
